import { Avatar, AvatarGroup, Box, Chip, Tooltip, Typography } from "@mui/material";
import React from "react";
import { stringToVintageColor } from "../utils";

export interface SenderProfile {
  id: string;
  name: string;
  email: string;
  picture?: string;
  warmupStats?: {
    health_score_label?: string;
    sent?: number;
    landed_inbox?: number;
    received?: number;
  };
}

interface SenderProfilesDisplayProps {
  profiles: SenderProfile[];
  selectedIds: string[];
  maxVisible?: number;
  maxNamesVisible?: number;
  size?: "small" | "medium";
  showEmails?: boolean;
  showNames?: boolean;
  onClick?: () => void;
}

const SenderProfilesDisplay: React.FC<SenderProfilesDisplayProps> = ({
  profiles,
  selectedIds,
  maxVisible = 3,
  maxNamesVisible = 2,
  size = "medium",
  showEmails = true,
  showNames = false,
  onClick,
}) => {
  const selectedProfiles = profiles.filter((p) => selectedIds.includes(p.id));
  const visibleProfiles = selectedProfiles.slice(0, maxVisible);
  const remainingCount = selectedProfiles.length - maxVisible;

  if (selectedProfiles.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ cursor: onClick ? "pointer" : "default" }}
        onClick={onClick}
      >
        No sender profiles selected
      </Typography>
    );
  }

  const avatarSize = size === "small" ? 28 : 36;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { opacity: 0.8 } : {},
      }}
      onClick={onClick}
    >
      <AvatarGroup
        max={maxVisible + 1}
        sx={{
          "& .MuiAvatar-root": {
            width: avatarSize,
            height: avatarSize,
            fontSize: size === "small" ? "0.75rem" : "0.875rem",
            border: "2px solid white",
          },
        }}
      >
        {visibleProfiles.map((profile) => (
          <Tooltip
            key={profile.id}
            title={
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {profile.name}
                </Typography>
                <Typography variant="caption">{profile.email}</Typography>
                {profile.warmupStats && (
                  <Typography variant="caption" display="block">
                    Health: {profile.warmupStats.health_score_label || "N/A"}
                  </Typography>
                )}
              </Box>
            }
          >
            <Avatar
              src={profile.picture}
              sx={{
                bgcolor: stringToVintageColor(profile.name),
              }}
            >
              {profile.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip
            title={
              <Box>
                {selectedProfiles.slice(maxVisible).map((p) => (
                  <Typography key={p.id} variant="body2">
                    {p.name} ({p.email})
                  </Typography>
                ))}
              </Box>
            }
          >
            <Avatar sx={{ bgcolor: "grey.500" }}>+{remainingCount}</Avatar>
          </Tooltip>
        )}
      </AvatarGroup>

      {showNames && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", minWidth: 0 }}>
          {selectedProfiles.slice(0, maxNamesVisible).map((profile, index) => (
            <Typography
              key={profile.id}
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {profile.name}{index < Math.min(selectedProfiles.length, maxNamesVisible) - 1 && ","}
            </Typography>
          ))}
          {selectedProfiles.length > maxNamesVisible && (
            <Tooltip
              title={
                <Box>
                  {selectedProfiles.slice(maxNamesVisible).map((p) => (
                    <Typography key={p.id} variant="body2">
                      {p.name}
                    </Typography>
                  ))}
                </Box>
              }
            >
              <Chip
                label={`+${selectedProfiles.length - maxNamesVisible} more`}
                size="small"
                variant="outlined"
                sx={{ height: 22, cursor: "pointer" }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {!showNames && showEmails && selectedProfiles.length <= 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {visibleProfiles.map((profile) => (
            <Typography
              key={profile.id}
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {profile.email}
            </Typography>
          ))}
        </Box>
      )}

      {!showNames && showEmails && selectedProfiles.length > 2 && (
        <Chip
          label={`${selectedProfiles.length} profiles`}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default SenderProfilesDisplay;
